import { AssetImage, OperatorWithOverrides } from "gql/generated";
import { useCallback, useEffect, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { getMultilingualString } from "shared/utils";
import { widgetOperatorOverridesImageSchema } from "./useWidgetOperatorOverridesForm";

type UseWidgetOperatorOverridesModalProps = {
  form: UseFormReturn<{
    shortDescription: string;
    description: string;
    images: {
      _id: string;
      caption: {
        en?: string | null | undefined;
        es?: string | null | undefined;
        fr?: string | null | undefined;
      };
      blurhash: string;
    }[];
  }>;
  operator: OperatorWithOverrides | undefined;
};

export const useWidgetOperatorOverridesModal = ({
  form,
  operator,
}: UseWidgetOperatorOverridesModalProps) => {
  /**
   * Get the initial image array
   * - This will be used for the Carousel and to determine if changes
   *   have been made to the selected featured image
   */
  const [imageIndex, setImageIndex] = useState(0);
  const [initialImages, setInitialImages] = useState<Partial<AssetImage>[]>([]);
  useEffect(() => {
    setImageIndex(0);
    setInitialImages(
      operator?.overrides?.images && operator.overrides.images.length > 0
        ? operator.overrides.images
        : (operator?.images ?? []),
    );
  }, [form, operator]);

  /**
   * Reorder the images array to put the selected image as the featured image
   * and keep the order of the remaining images
   */
  const handleFeaturedImageChange = useCallback(
    ({ index }: { index: number }) => {
      // Update the carousel index
      setImageIndex(index);

      // Update the imageOverrides array
      const featuredImage = initialImages[index];
      const imageOverrides = [
        featuredImage,
        ...initialImages.filter((image) => image._id !== featuredImage._id),
      ];

      // Update the form value
      const imageSchemas = imageOverrides.map((imageOverride) =>
        widgetOperatorOverridesImageSchema.parse(imageOverride),
      );
      form.setValue("images", imageSchemas);
    },
    [form, initialImages],
  );

  /**
   * Revert the overrides back to the default data
   */
  const handleRevertClick = () => {
    handleFeaturedImageChange({ index: 0 });
    form.setValue(
      "description",
      getMultilingualString(operator?.description ?? {}) ?? "",
      { shouldDirty: true },
    );
    form.setValue("images", operator?.images ?? [], { shouldDirty: true });
    form.setValue(
      "shortDescription",
      getMultilingualString(operator?.shortDescription ?? {}) ?? "",
      { shouldDirty: true },
    );
  };

  return {
    imageIndex,
    initialImages,
    handleFeaturedImageChange,
    handleRevertClick,
  };
};
